/* eslint-disable react/sort-comp */
/* eslint-disable max-len */
import { createRef, Component } from 'react';
import classNames from 'classnames';
import { withTranslation } from 'react-i18next';
import { Link, withRouter } from 'react-router-dom';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import Container from '@components/shared/Admin/Container';
import { Alert, notification, Result, Menu, Typography } from 'antd';
import { connect } from 'react-redux';
import { flow, isEmpty } from 'lodash/fp';
import { parse } from 'qs';
import routes from '@root/routes';
import api from '@shared/api';
import s from '@components/layouts/Admin/PropertyPage';
import Spinner from '@components/shared/Spinner';
import actions from '@store/actions';
import { locationType, historyType } from '@propTypes';
import css from './BuildingPage.module.less';
import MenuSwitcher, { TABS } from './MenuSwitcher';

const {
  api: {
    admin: { building: buildingRoute },
  },
  admin: { buildingSearch: buildingSearchRoute, buildingListings: buildingListingsRoute },
} = routes;
const STATES = {
  fetching: 'fetching',
  fetched: 'fetched',
  noBuilding: 'noBuilding',
};
type Props = {
  match: {
    params: {
      id: string;
    };
  };
  t: $TSFixMeFunction;
  displayBreadcrumbs: $TSFixMeFunction;
  removeBreadcrumbs: $TSFixMeFunction;
  location: locationType;
  history: historyType;
  flags: {};
};
type State = $TSFixMe;
class RawBuildingPage extends Component<Props, State> {
  buildingPage = createRef();

  state = {
    display: STATES.fetching,
    building: null,
    errors: [],
  };

  componentDidMount() {
    this.fetchAdminBuilding();
    this.setBreadcrumbs();
  }

  componentDidUpdate(prevProps: Props) {
    const {
      match: {
        params: { id: buildingId },
      },
    } = this.props;
    const {
      match: {
        params: { id: prevBuildingId },
      },
    } = prevProps;
    this.setBreadcrumbs();
    if (prevBuildingId !== buildingId) {
      this.handleRedirect();
    }
  }

  componentWillUnmount() {
    const { removeBreadcrumbs } = this.props;
    removeBreadcrumbs();
  }

  handleRedirect = () => {
    this.fetchAdminBuilding();
    this.scrollToTop();
  };

  scrollToTop = () => {
    (this as $TSFixMe).buildingPage.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  setBreadcrumbs = () => {
    const { displayBreadcrumbs, t } = this.props;
    const { building } = this.state;
    const breadcrumbs = [
      t('breadcrumbs.admin'),
      { content: t('breadcrumbs.buildings'), link: buildingSearchRoute },
    ];
    if (building) {
      // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
      breadcrumbs.push(this.renderBuildingName(building.primaryName, building));
    }
    displayBreadcrumbs(breadcrumbs);
  };

  fetchAdminBuilding = async () => {
    const {
      match: {
        params: { id: buildingId },
      },
    } = this.props;
    const response = await api.fetch(buildingRoute(buildingId));
    try {
      const building = await response.json();
      this.setState({ building, display: STATES.fetched });
    } catch (error) {
      this.setState({ display: STATES.noBuilding });
    }
  };

  updateBuilding = async params => {
    const {
      match: {
        params: { id: buildingId },
      },
      t,
    } = this.props;
    const response = await api.put(buildingRoute(buildingId), params);
    const json = await response.json();
    if (response.ok) {
      notification.info({ message: t('building.saveSuccess') });
      return this.setState({ building: json, errors: [] });
    }
    if (!isEmpty(json.errors)) {
      return this.setState({ errors: json.errors }, () => {
        this.scrollToTop();
      });
    }
    if (!isEmpty(json.messages)) {
      return notification.error({ message: json.messages.join(', ') });
    }
    return notification.error({ message: t('building.saveFailure') });
  };

  currentPage = () => {
    const { location } = this.props;
    const queryParams = parse(location.search, { ignoreQueryPrefix: true });
    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string | string[] | ParsedQs | P... Remove this comment to see the full error message
    return queryParams.tab && TABS.includes(queryParams.tab) ? queryParams.tab : 'form';
  };

  changePage = ({ key }) => {
    const {
      location: { pathname },
      history,
    } = this.props;
    history.push(`${pathname}?tab=${key}`);
  };

  renderBuildingName = (primaryName, building) =>
    [primaryName, building.secondaryName].filter(obj => obj).join(' - ');

  renderError = error => {
    const { building } = this.state;
    const { t } = this.props;
    if (error.field === 'status') {
      if ('missingFields' in error.metadata) {
        return (
          // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
          <Alert
            key={error.field}
            className={classNames(css.missingFieldsMessage, css.error)}
            icon={<ExclamationCircleFilled style={{ color: '#ff8044' }} />}
            showIcon
            type="error"
            description={
              <div>
                <div>{error.message}</div>
                <ul data-testid="missingFieldsList" className={css.missingFieldsList}>
                  {error.metadata.missingFields.map(missingField => (
                    <li key={missingField}>{missingField}</li>
                  ))}
                </ul>
              </div>
            }
          />
        );
      }
      return (
        // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
        <Alert
          key={error.field}
          className={css.error}
          showIcon
          type="error"
          description={
            <div>
              <div className={css.errorMessage}>{error.message}</div>
              <Link
                className={css.viewListedSpacesText}
                // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
                to={buildingListingsRoute(building.id)}
                target="_blank"
              >
                {t('building.viewListedSpaces')}
              </Link>
            </div>
          }
        />
      );
    }
    return (
      // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
      <Alert
        key={error.field}
        className={css.error}
        showIcon
        type="error"
        description={
          <div>
            <div>{error.message}</div>
          </div>
        }
      />
    );
  };

  render() {
    const { flags, t } = this.props;
    const { building, display, errors } = this.state;
    if (display === STATES.noBuilding) return <Result status="404" title="404" />;
    const hasErrors = !isEmpty(errors);
    return (
      // @ts-expect-error ts-migrate(2322) FIXME: Type 'RefObject<unknown>' is not assignable to typ... Remove this comment to see the full error message
      <div ref={this.buildingPage}>
        <Container>
          {display === STATES.fetching ? (
            <div className={s['spinner-wrapper']}>
              <Spinner />
            </div>
          ) : (
            <>
              <Typography.Title>
                {/* @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'. */}
                {this.renderBuildingName(building.primaryName, building)}
              </Typography.Title>
              {/* @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call. */}
              <Menu onClick={this.changePage} selectedKeys={[this.currentPage()]} mode="horizontal">
                {TABS.filter(tab => {
                  if (
                    (tab === 'amenities' || tab === 'dataCollection') &&
                    !flags['amenities-data-tabs']
                  ) {
                    return null;
                  }
                  return tab;
                }).map(tab => (
                  <Menu.Item key={tab}>{t(`building.menuItems.${tab}`)}</Menu.Item>
                ))}
              </Menu>
              {hasErrors ? errors.map(error => this.renderError(error)) : null}
              <MenuSwitcher
                building={building}
                updateBuilding={this.updateBuilding}
                currentPage={this.currentPage()}
                fetchAdminBuilding={this.fetchAdminBuilding}
                errors={errors}
              />
            </>
          )}
        </Container>
      </div>
    );
  }
}
export { RawBuildingPage };
export default withLDConsumer()(
  flow(
    withRouter,
    withTranslation('admin'),
    connect(
      () => ({}),
      dispatch => ({
        displayBreadcrumbs(breadcrumbs) {
          dispatch(actions.addBreadcrumbs(breadcrumbs));
        },
        removeBreadcrumbs() {
          dispatch(actions.removeBreadcrumbs());
        },
      }),
    ),
  )(RawBuildingPage),
);
